import React, { useState, useEffect } from 'react';
import { message, Form, Input } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import providerService, { PROVIDER_FILE_TYPES } from 'services/providers.service';
import RadioCards from 'components/RadioCards/RadioCards';
import { DatePicker } from 'components/ui/DatePicker';
import { validateDates } from 'utils';
import UploadFile from 'components/UploadFile/UploadFile';
import dayjs from 'dayjs';
import { DATE_FORMAT_US_SLASH } from 'components/AriaDatePicker/AriaDatePicker';
import ExternalLink from 'components/ExternalLink/ExternalLink';

const AirForceHCCAccreditations = ({ application, applicationFiles, setApplicationFiles, isEditable = false }) => {
  const [fetchedAccreditations, setFetchedAccreditations] = useState([]);
  const [accreditationForm] = Form.useForm();
  const accredited = Form.useWatch('accredited', accreditationForm);

  useEffect(() => {
    const fetchAccreditations = async () => {
      try {
        const accreditations = await providerService.getAccreditations(application.id);
        setFetchedAccreditations(accreditations);
      } catch (error) {
        console.error('Error fetching accreditations', error);
      }
    };
    fetchAccreditations();
  }, [application.id]);

  const nafccAccreditation = fetchedAccreditations.find((acc) => acc?.type?.id === 2); // NAFCC type id is 2

  const { isEditing: isEditingAccreditation, editProps: accreditationEditProps } = useEditCard({
    id: 'nafcc-accreditation',
    onEditStart: () => {
      accreditationForm.setFieldsValue({
        accredited: !!nafccAccreditation,
        accreditationNumber: nafccAccreditation?.accreditationNumber,
        startDate: nafccAccreditation?.startDate ? dayjs(nafccAccreditation.startDate).format('YYYY-MM-DD') : '',
        endDate: nafccAccreditation?.endDate ? dayjs(nafccAccreditation.endDate).format('YYYY-MM-DD') : '',
      });
    },
    onSave: async () => {
      await accreditationForm.validateFields();
      const values = accreditationForm.getFieldsValue();

      const nafccType = {
        id: 2,
        title: 'NAFCC',
        description: 'National Association of Family Child Care',
        doesNotExpire: false,
        ageRangeRequired: false,
        sequenceNumber: 14,
        hasSelfStudy: true,
        homeOnly: true,
      };

      if (!values.accredited && nafccAccreditation) {
        await providerService.deleteAccreditation(application.id, nafccAccreditation.id);
        setFetchedAccreditations([]);
      } else if (values.accredited) {
        const payload = {
          ...(nafccAccreditation?.id && { id: nafccAccreditation.id }),
          accreditationNumber: values.accreditationNumber,
          startDate: values.startDate,
          endDate: values.endDate,
          application: { id: application.id },
          type: nafccType,
        };
        const updatedAccreditation = await providerService.addUpdateAccreditation(application.id, payload);
        setFetchedAccreditations([updatedAccreditation]);
      }

      accreditationForm.resetFields();
      message.success('Accreditation updated');
    },
    onDiscard: () => accreditationForm.resetFields(),
  });

  return (
    <>
      <Card
        title="Accreditation"
        extra={isEditable && <CardEdit {...accreditationEditProps} />}
        noBodyPadding
        stickyHeader
      >
        <Form form={accreditationForm} disabled={accreditationEditProps.loading}>
          <TabularList
            indented
            data={[
              {
                title: 'Do you have a NAFCC accreditation?',
                titleRequired: true,
                value: isEditingAccreditation ? (
                  <Form.Item
                    name="accredited"
                    className="mb-0"
                    rules={[{ required: true, message: 'Please specify whether you have a NAFCC accreditation' }]}
                  >
                    <RadioCards
                      size="small"
                      options={[
                        { value: true, label: 'Yes' },
                        { value: false, label: 'No' },
                      ]}
                    />
                  </Form.Item>
                ) : nafccAccreditation ? (
                  'Yes'
                ) : (
                  'No'
                ),
              },
              ...((isEditingAccreditation && accredited) || (!isEditingAccreditation && nafccAccreditation)
                ? [
                    {
                      title: 'NAFCC Accreditation number',
                      titleRequired: true,
                      value: isEditingAccreditation ? (
                        <Form.Item
                          name="accreditationNumber"
                          className="mb-0"
                          rules={[{ required: true, message: 'Accreditation number is required' }]}
                        >
                          <Input placeholder="Enter accreditation number" className="max-w-96" />
                        </Form.Item>
                      ) : (
                        nafccAccreditation?.accreditationNumber
                      ),
                    },
                    {
                      title: 'Start date',
                      titleRequired: true,
                      value: isEditingAccreditation ? (
                        <Form.Item
                          name="startDate"
                          className="mb-0"
                          dependencies={['endDate']}
                          rules={[
                            { required: true, message: 'Start date is required' },
                            {
                              validator: validateDates(
                                accreditationForm.getFieldValue,
                                'startDate',
                                'endDate',
                                'Start date must be before expiration date',
                              ),
                            },
                          ]}
                        >
                          <DatePicker className="max-w-96" />
                        </Form.Item>
                      ) : nafccAccreditation?.startDate ? (
                        dayjs(nafccAccreditation.startDate).format(DATE_FORMAT_US_SLASH)
                      ) : (
                        '—'
                      ),
                    },
                    {
                      title: 'Expiration date',
                      titleRequired: true,
                      value: isEditingAccreditation ? (
                        <Form.Item
                          name="endDate"
                          className="mb-0"
                          dependencies={['startDate']}
                          rules={[
                            { required: true, message: 'Expiration date is required' },
                            {
                              validator: validateDates(
                                accreditationForm.getFieldValue,
                                'startDate',
                                'endDate',
                                'Expiration date must be after start date',
                              ),
                            },
                          ]}
                        >
                          <DatePicker className="max-w-96" />
                        </Form.Item>
                      ) : nafccAccreditation?.endDate ? (
                        dayjs(nafccAccreditation.endDate).format(DATE_FORMAT_US_SLASH)
                      ) : (
                        '—'
                      ),
                    },
                    {
                      title: 'NAFCC accreditation certificate',
                      titleRequired: true,
                      value: (
                        <UploadFile
                          fileCode={PROVIDER_FILE_TYPES.NATIONAL_ACCREDITATION.code}
                          files={applicationFiles}
                          setFiles={setApplicationFiles}
                          applicationId={application?.id}
                          readOnly={!isEditable}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Form>
      </Card>

      <Card title="Education" noBodyPadding stickyHeader>
        <TabularList
          indented
          data={[
            {
              title: 'High school diploma, GED, or high school self-certification statement',
              titleRequired: true,
              value: (
                <>
                  <UploadFile
                    fileCode={PROVIDER_FILE_TYPES.DIPLOMA.code}
                    files={applicationFiles}
                    setFiles={setApplicationFiles}
                    applicationId={application?.id}
                    readOnly={!isEditable}
                  />
                  <div className="text-sm text-gray-600 mt-2">
                    If you're unable to obtain your high school diploma or transcripts download and complete the{' '}
                    <ExternalLink
                      text="high school self-certification statement document"
                      href="/docs/self_certification_statement.pdf"
                    />
                    . Acceptable formats: pdf, png, jpg
                  </div>
                </>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
};

export default AirForceHCCAccreditations;
